import { useId, useState } from 'react';
import { LogOut } from 'lucide-react';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

interface SignOutButtonProps {
  iconOnly?: boolean;
  className?: string;
  /** Red, for the settings page, where signing out sits among harmless controls. */
  destructive?: boolean;
  /** Ask first. Signing out is one click away from losing unsaved work. */
  confirm?: boolean;
}

/**
 * Uses a normal Rails form submission so sign-out still follows Devise's
 * redirect even when fetch/SPA state is unhealthy. This is also usable on the
 * no-school screen, where the shared app chrome is intentionally unavailable.
 *
 * The confirm dialog renders in a portal, outside the form, so its confirm
 * button reaches the form by id via the HTML `form` attribute rather than by
 * being nested inside it.
 */
export default function SignOutButton({
  iconOnly = false,
  className,
  destructive = false,
  confirm = false,
}: SignOutButtonProps) {
  const csrfToken = document
    .querySelector('meta[name="csrf-token"]')
    ?.getAttribute('content') ?? '';
  const formId = useId();
  const [confirming, setConfirming] = useState(false);

  const variant = destructive ? 'destructive' : iconOnly ? 'ghost' : 'outline';
  const buttonClassName = iconOnly
    ? 'text-muted-foreground hover:text-foreground'
    : 'w-full';

  return (
    <>
      <form
        id={formId}
        method="post"
        action="/users/sign_out"
        className={iconOnly ? undefined : className}
      >
        <input type="hidden" name="_method" value="delete" />
        <input type="hidden" name="authenticity_token" value={csrfToken} />
        <Button
          type={confirm ? 'button' : 'submit'}
          onClick={confirm ? () => setConfirming(true) : undefined}
          variant={variant}
          size={iconOnly ? 'icon' : 'default'}
          aria-label="Sign out"
          className={buttonClassName}
        >
          <LogOut className="h-5 w-5" />
          {!iconOnly && 'Sign out'}
        </Button>
      </form>

      {confirm && (
        <AlertDialog open={confirming} onOpenChange={setConfirming}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Sign out?</AlertDialogTitle>
              <AlertDialogDescription>
                You will need to sign in again to get back in. Anything you have not saved will be
                lost.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Stay signed in</AlertDialogCancel>
              <Button
                type="submit"
                form={formId}
                variant="destructive"
                className="gap-2"
              >
                <LogOut className="h-4 w-4" />
                Sign out
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
